import Phaser from 'phaser';
import { GameScene, type PlayerRenderObjects } from '../GameScene';
import type { PlayerStateView } from '../../state/GameStateView';
import { TILE_SIZE, MOVEMENT_CONFIG, FOOTSTEP_BASE_DURATION_MS } from '@shared/constants';
import { Direction, EntityState } from '@shared/types/entity';
import { getFactionColor, type FactionId } from '@shared/data/factions';
import { getNakedBodyId, type CharacterRaceId, type CharacterSexId } from '@shared/types/character';
import {
  ensureBodyReady,
  getBodyAnimKey,
  getBodyColumns,
  getBodyIdleFrame,
  getBodyLateralColumns,
  getBodySpriteKey,
  warnBadBodyAnim,
} from '../../sprites/BodySpriteManager';
import { breathingPhaseOffset } from '../../util/hash';
import { getHeadFrameForDirection, getHeadSpriteKey } from '../../sprites/HeadSpriteManager';
import { getBootFrameForDirection, getBootSpriteKey } from '../../sprites/BootSpriteManager';
import { getHelmetFrameForDirection, getHelmetSpriteKey } from '../../sprites/HelmetSpriteManager';
import {
  getShieldFrameFromBodyIndex,
  getShieldIdleFrame,
  getShieldSpriteKey,
} from '../../sprites/ShieldSpriteManager';
import {
  getWeaponFrameFromBodyIndex,
  getWeaponIdleFrame,
  getWeaponSheetOffsetForTextureKey,
  getWeaponSpriteKey,
} from '../../sprites/WeaponSpriteManager';
import { DEFAULT_PLAYER_FOOTSTEP_ID } from '../../sprites/FootstepSpriteManager';

/**
 * Sincronización de render de PLAYERS: crea/actualiza el conjunto de sprites de un jugador
 * (body/head/weapon/shield/helm/boot, animaciones, sombra, luz, label, depth, montura/barco…) desde
 * su PlayerState (addOrUpdatePlayer, el método más grande del scene), calcula el nakedBodyId por
 * raza/sexo y limpia todo al salir (removePlayer). Extraído de GameScene (god-object). Todo el estado
 * de render vive en GameScene (host) y se accede vía `this.scene.*`. Ver docs/SPRITE_SYSTEM.md,
 * docs/MOVEMENT_AND_PREDICTION.md.
 */
export class PlayerSyncController {
  constructor(private scene: GameScene) {}

  public addOrUpdatePlayer(id: string, player: PlayerStateView): void {
    const existing = this.scene.playerObjects.get(id);

    // El servidor maneja coordenadas de grilla; el cliente las convierte a píxeles

    const worldX = player.x * TILE_SIZE + TILE_SIZE / 2;

    const worldY = player.y * TILE_SIZE + TILE_SIZE / 2;

    // Posiciones visuales ajustadas

    const bodyY = worldY + GameScene.BODY_OFFSET_Y;

    const bodyBottomY = bodyY + GameScene.BODY_FRAME_HEIGHT / 2;

    const headCenterY =
      bodyBottomY -
      GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM -
      GameScene.HEAD_FRAME_HEIGHT / 2;

    const isLocal = this.scene.localSessionId === id;

    // Usar los IDs de apariencia que envía el servidor
    // bodyId = body actual (naked si no tiene armadura, o armor si tiene)
    // El servidor ya se encarga de calcular el bodyId correcto
    const rawBodyId = (player as any).bodyId as number | undefined;
    const rawHeadId = (player as any).headId as number | undefined;
    const rawHelmId = (player as any).helmId as number | undefined;
    const rawBootId = (player as any).bootId as number | undefined;

    // Fallback: si el servidor no envía bodyId, usar naked body según raza/sexo
    const bodyId = rawBodyId ?? this.calculateNakedBodyId((player as any).race, (player as any).sex);
    const headId = rawHeadId ?? 1;
    const helmId = rawHelmId ?? 0;
    const bootId = rawBootId ?? 0;
    const shieldId = ((player as any).shieldId as number | undefined) ?? 0;
    const weaponId = ((player as any).weaponId as number | undefined) ?? 0;

    const now = this.scene.time.now;

    if (!existing) {
      console.log(`[GameScene] Creating player: bodyId=${bodyId}, race=${(player as any).race}, sex=${(player as any).sex}, isLocal=${isLocal}`);
      
      // =====================================================================
      // ALL PLAYERS: Use world coordinates with scrollFactor(1).
      // This ensures proper depth sorting with decorations.
      // The camera follows the local player via updateCameraPosition().
      // =====================================================================
      const initialX = worldX;
      const initialBodyBottomY = worldY + GameScene.BODY_OFFSET_Y + GameScene.BODY_FRAME_HEIGHT / 2;
      
      // Create body sprite in world coordinates
      // Create body sprite directly in scene for proper depth sorting.
      // AUTO-REPARACIÓN: garantizar que el body esté cortado ANTES de crear el sprite, así nunca se
      // muestra la lámina entera aunque el warmup async todavía no lo haya cortado (ver ensureBodyReady).
      ensureBodyReady(this.scene, bodyId);
      const sprite = this.scene.add.sprite(initialX, initialBodyBottomY, getBodySpriteKey(bodyId));
      sprite.setOrigin(0.5, 1);
      sprite.setFrame(getBodyIdleFrame(player.direction, bodyId));
      sprite.setScrollFactor(1);

      // Shadow depth is below the body
      const shadowDepth = this.scene.getEntityBaseDepthFromTileY(player.y) + GameScene.SHADOW_DEPTH_DELTA;

      // Create simple elliptical shadow below feet
      const shadowSprite = this.scene.entityLight.createShadowSprite(
        initialX,
        initialBodyBottomY,
        shadowDepth,
        1.0 // Scale multiplier
      );

      // Create dropshadow (independent system, scales with body width)
      const dropshadowSprite = this.scene.shadows.createDropshadowSprite(
        initialX,
        initialBodyBottomY,
        shadowDepth,
        bodyId
      );

      // Camera target for camera control (not in container)
      const cameraTarget = isLocal ? this.scene.add.zone(worldX, initialBodyBottomY, 1, 1) : null;
      if (cameraTarget) {
        cameraTarget.setOrigin(0.5, 0.5);
        cameraTarget.setDepth(-10000);
        cameraTarget.setScrollFactor(1);
      }

      // Calculate Y positions for equipment
      const headOffsetFromFeet = GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM + GameScene.HEAD_FRAME_HEIGHT / 2;
      const initialHeadCenterY = initialBodyBottomY - headOffsetFromFeet;
      
      // Head sprite
      const headSprite =
        headId > 0
          ? this.scene.add.sprite(initialX, initialHeadCenterY, getHeadSpriteKey(headId)).setOrigin(0.5, 0.5).setScrollFactor(1)
          : null;

      if (headSprite) {
        headSprite.setFrame(getHeadFrameForDirection(player.direction));
      }

      // Helmet (8px above head)
      const helmCenterY = initialHeadCenterY - 8;
      const helmSprite =
        helmId > 0
          ? this.scene.add.sprite(initialX, helmCenterY, getHelmetSpriteKey(helmId)).setOrigin(0.5, 0.5).setScrollFactor(1)
          : null;

      if (helmSprite) {
        helmSprite.setFrame(getHelmetFrameForDirection(player.direction));
      }

      // Boots
      const bootSprite =
        bootId > 0
          ? this.scene.add.sprite(initialX, initialBodyBottomY, getBootSpriteKey(bootId)).setOrigin(0.5, 1).setScrollFactor(1)
          : null;

      if (bootSprite) {
        bootSprite.setFrame(getBootFrameForDirection(player.direction));
      }

      // Shield and weapon
      const shieldSprite =
        shieldId > 0
          ? this.scene.add
              .sprite(initialX + GameScene.SHIELD_OFFSET_X, initialBodyBottomY, getShieldSpriteKey(shieldId))
              .setOrigin(0.5, 1)
              .setScrollFactor(1)
          : null;

      if (shieldSprite) {
        shieldSprite.setFrame(getShieldIdleFrame(player.direction));
      }

      const weaponSprite =
        weaponId > 0
          ? this.scene.add
              .sprite(
                initialX +
                  GameScene.weaponOffsetX(player.direction) +
                  getWeaponSheetOffsetForTextureKey(getWeaponSpriteKey(weaponId)).x,
                initialBodyBottomY +
                  GameScene.WEAPON_OFFSET_Y +
                  getWeaponSheetOffsetForTextureKey(getWeaponSpriteKey(weaponId)).y,
                getWeaponSpriteKey(weaponId)
              )
              .setOrigin(0.5, 1)
              .setScrollFactor(1)
          : null;

      if (weaponSprite) {
        weaponSprite.setFrame(getWeaponIdleFrame(player.direction));
      }

      const playerBaseDepth = this.scene.getEntityBaseDepthFromTileY(player.y);
      const labelDepth = playerBaseDepth + GameScene.LABEL_DEPTH_DELTA;

      const label = this.scene.labels.addPlayerNameText(initialX, initialBodyBottomY + 2, player.name || 'Player', labelDepth, this.scene.labels.labelColorForPlayerId(id));
      if (label) {
        label.setScrollFactor(1);
      }

      // Heading arrow - positioned at tile center
      const arrow = this.scene.add.sprite(initialX, worldY, 'headingArrow');
      arrow.setScrollFactor(1);
      arrow.setOrigin(0.5, 0.5);
      arrow.setScale(0.5);
      this.scene.updateArrowOrientation(arrow, player.direction);
      arrow.setVisible(this.scene.showHeadingArrow);

      // All sprites are added directly to scene - Phaser handles depth sorting automatically

      const renderObj: PlayerRenderObjects = {
        sprite,
        castShadow: this.scene.shadows.createCastShadowLayers(),
        shadow: shadowSprite,
        dropshadow: dropshadowSprite,
        shadowFade: 1,
        head: headSprite,
        helm: helmSprite,
        boots: bootSprite,
        label,
        arrow,
        shield: shieldSprite,
        weapon: weaponSprite,
        cameraTarget,
        bodyId,
        bodyCenterOffsetX: GameScene.getBodyCenterOffsetX(bodyId),
        activeAnimations: [],
        lastTileX: player.x,
        lastTileY: player.y,
        currentWorldX: worldX,
        currentWorldY: worldY,
        targetWorldX: worldX,
        targetWorldY: worldY,
        startWorldX: worldX,
        startWorldY: worldY,
        moveStartTime: 0,
        moveDuration: 0,
        moveElapsed: 0,
        moveSpeed: 0,
        stepQueue: [],
        serverTileX: player.x,
        serverTileY: player.y,
        activeAxis: null,
        cornerReady: false,
        lastUpdateTime: 0,
        walkUntil: 0,
        visualDirection: player.direction,
        pendingDirection: null,
        lastDirection: player.direction,
        isMoving: false,
        breathingOffset: breathingPhaseOffset(id), // determinístico por id → sincronizado entre clientes
        idleSince: null, // se setea cuando la entidad queda idle (gate de 5s antes de respirar)
        previousHp: null,
        previousMana: null,
        soul: null,
      };

      this.scene.updateEquipmentDepth(renderObj, player.direction, player.y);

      // Hacer sprite interactivo para clicks (mostrar nombre en consola)
      sprite.setInteractive({ useHandCursor: false });
      sprite.on('pointerdown', () => {
        console.log(`[GameScene] Clicked on player: ${player.name || 'Unknown'}`);
      });

      // Inicializar valores previos de HP/Mana con los valores actuales del jugador
      renderObj.previousHp = player.minHp ?? 0;
      renderObj.previousMana = player.minMana ?? 0;

      this.scene.playerObjects.set(id, renderObj);
      
      // Register entity light source
      this.scene.entityLight.registerEntityLight(id, initialX, initialBodyBottomY);

      // =====================================================================
      // RENDERING FIX: Configure camera follow ONLY ONCE when player is created.
      // Using lerp 0.5 for smooth but responsive following that works well
      // with pixel art. Higher values = more responsive, lower = smoother but laggier.
      // The camera will track the cameraTarget Zone, which is updated each frame
      // with the interpolated player position.
      // =====================================================================
      if (isLocal) {
        console.log('[GameScene] Creating new local player sprite, configuring camera...');

        if (!cameraTarget) {
          console.error('[GameScene] cameraTarget is null for local player! This should never happen');
        }
        this.scene.localPlayerFollowTarget = cameraTarget!;

        // =====================================================================
        // RENDERING FIX: We no longer use startFollow() here.
        // Camera position is now controlled directly in updateCameraPosition()
        // which is called every frame for perfect synchronization.
        // =====================================================================
        this.scene.localPlayerFollowTarget = cameraTarget!;
        
        // Stop any existing follow to ensure clean state
        const cam = this.scene.cameras.main;
        cam.stopFollow();

        console.log(
          '[GameScene] Camera configured for local player (direct control), sessionId:',
          id
        );

        this.scene.loadingScreenUi.checkAndHideLoadingScreen();
      }
    } else {
      // =====================================================================
      // RENDERING FIX: Camera follow should only be configured ONCE, not every frame.
      // Previously this block called startFollow() on every update which caused
      // camera jitter and inconsistent tracking. Now we only configure the camera
      // if it's not already following the correct target.
      // =====================================================================
      if (isLocal) {
        // Track the camera target but don't use startFollow
        // Camera position is controlled directly in updateCameraPosition()
        if (this.scene.localPlayerFollowTarget !== existing.cameraTarget) {
          this.scene.localPlayerFollowTarget = existing.cameraTarget;
        }

        // Verificar si todo está listo para ocultar la pantalla de carga
        this.scene.loadingScreenUi.checkAndHideLoadingScreen();
      }

      // Detectar ganancias de HP/Mana y mostrar texto flotante (sin animación)
      // Las animaciones de set bonus se manejan desde el servidor via PLAY_ANIMATION
      const currentHp = player.minHp ?? 0;
      const currentMana = player.minMana ?? 0;

      if (existing.previousHp !== null && currentHp > existing.previousHp) {
        const hpGain = Math.round(currentHp - existing.previousHp);
        if (hpGain >= 1) {
          this.scene.overheadText.showHealthManaGainText(id, hpGain, true, true);
        }
      }

      if (existing.previousMana !== null && currentMana > existing.previousMana) {
        const manaGain = Math.round(currentMana - existing.previousMana);
        if (manaGain >= 1) {
          this.scene.overheadText.showHealthManaGainText(id, manaGain, false, true);
        }
      }
      
      // Actualizar valores previos
      existing.previousHp = currentHp;
      existing.previousMana = currentMana;

      const serverTileX = player.x;
      const serverTileY = player.y;

      const positionChanged = serverTileX !== existing.serverTileX || serverTileY !== existing.serverTileY;

      // =====================================================================
      // NOTA: Para jugadores remotos, usamos SERVER_STEP_MS como duración base.
      // El update loop se encargará de dividir por moveSpeed para calcular
      // la velocidad de interpolación correcta.
      // 
      // Para el jugador LOCAL, la duración ya está calculada correctamente
      // al inicio del movimiento en startLocalMovement().
      // =====================================================================

      // =====================================================================
      // ESTILO ARGENTUM ONLINE ORIGINAL: El servidor NO envía posición al 
      // jugador que se movió (ToPCAliveAreaButIndex). El cliente es autoritativo
      // para su propia posición.
      // 
      // Como nuestro servidor SÍ envía la posición, la solución es que el cliente
      // IGNORE COMPLETAMENTE cualquier actualización de posición del servidor
      // para el jugador local. Solo tracking de serverTileX/Y para debug.
      // =====================================================================
      const isLocalPlayer = id === this.scene.localSessionId;
      // Provocado (taunt): el server maneja el movimiento del player, así que el
      // jugador local deja de ser autoritativo y sigue la posición del server como
      // una entidad remota (interpolación suave). Ver docs/TAUNT.md.
      const isTaunted = ((player as unknown as { taunted?: number }).taunted ?? 0) === 1;
      let skipServerReconciliation = false;

      if (isLocalPlayer && !isTaunted) {
        // =====================================================================
        // JUGADOR LOCAL: IGNORAR TODO del servidor
        // =====================================================================
        // El cliente tiene autoridad total sobre su propia posición.
        // Solo actualizamos serverTileX/Y para el tile rojo de debug.
        // NO modificamos currentWorldX/Y, targetWorldX/Y, ni nada visual.
        // =====================================================================
        existing.serverTileX = serverTileX;
        existing.serverTileY = serverTileY;
        
        // ÚNICA excepción: teleport masivo (cambio de mapa, spawn, etc.)
        const distFromClient = Math.abs(existing.lastTileX - serverTileX) + 
                               Math.abs(existing.lastTileY - serverTileY);
        if (distFromClient > MOVEMENT_CONFIG.RECONCILE_THRESHOLD_TILES) {
          // Teleport - sincronizar con el servidor
          const teleportX = serverTileX * TILE_SIZE + TILE_SIZE / 2;
          const teleportY = serverTileY * TILE_SIZE + TILE_SIZE / 2;
          existing.currentWorldX = teleportX;
          existing.currentWorldY = teleportY;
          existing.targetWorldX = teleportX;
          existing.targetWorldY = teleportY;
          existing.startWorldX = teleportX;
          existing.startWorldY = teleportY;
          existing.lastTileX = serverTileX;
          existing.lastTileY = serverTileY;
          existing.moveDuration = 0;
          existing.moveSpeed = 0;
          existing.moveElapsed = 0;
        }
        
        // Saltar TODA la lógica de reconciliación
        skipServerReconciliation = true;
      }
      
      // =====================================================================
      // SOLO PARA JUGADORES REMOTOS: Lógica de reconciliación del servidor
      // =====================================================================
      if (!skipServerReconciliation && player.state === EntityState.IDLE) {
        // Jugador REMOTO en IDLE - sincronizar con servidor
        // =====================================================================
        // IMPORTANTE: Si el remoto tiene interpolación activa o pasos en cola,
        // NO interferir. El servidor manda IDLE ~200ms después del último step,
        // justo cuando el cliente aún está interpolando ese paso. Interrumpir
        // aquí causaría "saltos" visibles entre tiles.
        // Solo aplicar corrección cuando realmente terminó de moverse.
        // =====================================================================
        const hasActiveMovement = existing.moveDuration > 0 || existing.stepQueue.length > 0;

        if (!hasActiveMovement) {
          const dx = worldX - existing.currentWorldX;
          const dy = worldY - existing.currentWorldY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 1) {
            existing.startWorldX = existing.currentWorldX;
            existing.startWorldY = existing.currentWorldY;
            existing.targetWorldX = worldX;
            existing.targetWorldY = worldY;
            existing.moveElapsed = 0;
            existing.moveDuration = (distance / TILE_SIZE) * GameScene.SERVER_STEP_MS;
          } else {
            existing.currentWorldX = worldX;
            existing.currentWorldY = worldY;
            existing.targetWorldX = worldX;
            existing.targetWorldY = worldY;
            existing.moveDuration = 0;
          }

          existing.moveSpeed = 0;
          existing.activeAxis = null;
          existing.cornerReady = true;
          existing.stepQueue = [];
          existing.serverTileX = serverTileX;
          existing.serverTileY = serverTileY;
          existing.visualDirection = player.direction;
          existing.pendingDirection = null;
        }
      } else if (positionChanged && !skipServerReconciliation) {
        // Un paso del server: agregar a la cola o actualizar target
        existing.serverTileX = serverTileX;
        existing.serverTileY = serverTileY;
        const targetX = worldX;
        const targetY = worldY;
        
        // =====================================================================
        // ESTILO ARGENTUMONLINE.IO: EL JUGADOR LOCAL NUNCA SE RECONCILIA
        // =====================================================================
        // En argentumonline.io, el cliente confía en sus propios cálculos.
        // El servidor solo envía posiciones para OTROS jugadores.
        // El jugador local SIEMPRE usa su propia posición, excepto para
        // teleports muy grandes (cambio de mapa, etc.)
        // =====================================================================
        const isLocalPlayer2 = id === this.scene.localSessionId;
        
        // Calcular distancia desde la posición MÁS ADELANTADA conocida al nuevo target.
        // Usar currentWorldX (posición interpolada visual) causaba falsos positivos:
        // si el remoto apretaba 2 teclas rápido, la distancia desde la posición visual
        // al segundo target era >1 tile y disparaba corrección suave (100ms) = salto.
        // Usando el target de interpolación (o último en cola), 2 steps seguidos dan
        // distancia = 1 tile, que es lo esperado.
        let referenceX = existing.currentWorldX;
        let referenceY = existing.currentWorldY;
        if (existing.moveDuration > 0) {
          referenceX = existing.targetWorldX;
          referenceY = existing.targetWorldY;
        }
        if (existing.stepQueue.length > 0) {
          const lastStep = existing.stepQueue[existing.stepQueue.length - 1];
          referenceX = lastStep.x * TILE_SIZE + TILE_SIZE / 2;
          referenceY = lastStep.y * TILE_SIZE + TILE_SIZE / 2;
        }
        const dxFromRef = targetX - referenceX;
        const dyFromRef = targetY - referenceY;
        const distFromRef = Math.abs(dxFromRef) + Math.abs(dyFromRef);
        const distTiles = distFromRef / TILE_SIZE;
        
        if (isLocalPlayer2) {
          // Para el jugador local: SOLO hacer teleport si es MUY grande (cambio de mapa)
          // Cualquier otra diferencia se ignora - el cliente tiene autoridad
          if (distTiles > MOVEMENT_CONFIG.RECONCILE_THRESHOLD_TILES) {
            // Teleport - esto es probablemente cambio de mapa o spawn
            existing.currentWorldX = targetX;
            existing.currentWorldY = targetY;
            existing.targetWorldX = targetX;
            existing.targetWorldY = targetY;
            existing.startWorldX = targetX;
            existing.startWorldY = targetY;
            existing.lastTileX = serverTileX;
            existing.lastTileY = serverTileY;
            existing.moveSpeed = 0;
            existing.moveDuration = 0;
            existing.moveElapsed = 0;
          }
          // Siempre actualizar serverTileX/Y para tracking
          existing.serverTileX = serverTileX;
          existing.serverTileY = serverTileY;
          skipServerReconciliation = true;
        }

        // =====================================================================
        // RECONCILIACIÓN SOLO PARA JUGADORES REMOTOS
        // =====================================================================
        
        // Si la distancia es muy grande (> RECONCILE_THRESHOLD_TILES), snap/teleport directo
        if (!skipServerReconciliation && distTiles > MOVEMENT_CONFIG.RECONCILE_THRESHOLD_TILES) {
          existing.currentWorldX = targetX;
          existing.currentWorldY = targetY;
          existing.targetWorldX = targetX;
          existing.targetWorldY = targetY;
          existing.moveSpeed = 0;
          existing.moveElapsed = 0;
          existing.activeAxis = null;
          existing.cornerReady = true;
          existing.stepQueue = [];
          existing.serverTileX = serverTileX;
          existing.serverTileY = serverTileY;
          existing.visualDirection = player.direction;
          existing.pendingDirection = null;
          skipServerReconciliation = true;
        }

        // Si hay desincronización menor (1-2 tiles), usar corrección SUAVE
        if (!skipServerReconciliation && distTiles > 1.0) {
          existing.startWorldX = existing.currentWorldX;
          existing.startWorldY = existing.currentWorldY;
          existing.targetWorldX = targetX;
          existing.targetWorldY = targetY;
          existing.moveElapsed = 0;
          existing.moveDuration = MOVEMENT_CONFIG.SMOOTH_CORRECT_MS;
          existing.moveSpeed = 0;
          existing.activeAxis = null;
          existing.stepQueue = [];
          existing.serverTileX = serverTileX;
          existing.serverTileY = serverTileY;
          existing.visualDirection = player.direction;
          existing.pendingDirection = null;
          skipServerReconciliation = true;
        }
        
        // Para jugadores REMOTOS o cuando NO hay movimiento activo local:
        if (!skipServerReconciliation) {
          // Calcular distancia desde posición ACTUAL al target ACTUAL de interpolación
          const dxToTarget = existing.targetWorldX - existing.currentWorldX;
          const dyToTarget = existing.targetWorldY - existing.currentWorldY;
          const distToCurrentTarget = Math.abs(dxToTarget) + Math.abs(dyToTarget);
          
          // Si ya estamos cerca del target actual (menos de 3 pixels), actualizar target directamente
          // Si no, agregar a la cola para procesarlo después
          if (distToCurrentTarget < 3) {
            // =====================================================================
            // DIRECT UPDATE: Ya llegamos al target anterior, actualizar al nuevo
            // Solo para jugadores REMOTOS - el local ya fue manejado arriba
            // =====================================================================
            existing.startWorldX = existing.currentWorldX;
            existing.startWorldY = existing.currentWorldY;
            existing.targetWorldX = targetX;
            existing.targetWorldY = targetY;
            existing.moveElapsed = 0;
            // Para remotos, usar duración con buffer para absorber jitter de red
            existing.moveDuration = GameScene.REMOTE_STEP_MS;
            existing.moveSpeed = GameScene.PLAYER_MOVE_SPEED;
            const absDx = Math.abs(targetX - existing.startWorldX);
            const absDy = Math.abs(targetY - existing.startWorldY);
            existing.activeAxis = absDx >= absDy ? 'x' : 'y';
            existing.cornerReady = false;
          } else {
            // =====================================================================
            // QUEUE: Todavía interpolando, agregar a cola (máximo 2 pasos en cola)
            // =====================================================================
            if (existing.stepQueue.length < GameScene.MAX_STEP_QUEUE) {
              existing.stepQueue.push({ x: serverTileX, y: serverTileY, dir: player.direction });
            }
            // Si la cola está llena, ignorar (el cliente está muy atrasado, se sincronizará después)
          }
          
          existing.visualDirection = player.direction;
          existing.pendingDirection = null;
          
          // Asegurar que la animación de caminata continúe al menos un ciclo completo.
          // Solo para REMOTOS: el jugador local controla su `walkUntil` desde
          // startLocalMovement (gracia chica). Aplicar acá los 500ms al local dejaría
          // una cola de ~500ms caminando en el lugar tras soltar la tecla.
          if (id !== this.scene.localSessionId) {
            const WALK_ANIMATION_CYCLE_MS = 500;
            existing.walkUntil = Math.max(existing.walkUntil, now + WALK_ANIMATION_CYCLE_MS);
          }
        }
      } else {
        // Sin cambio de tile: sólo actualizar heading si no hay interpolación activa
        if (existing.activeAxis === null && existing.visualDirection !== player.direction) {
          existing.visualDirection = player.direction;
        }
      }

      // Actualizar la última dirección recibida del servidor (para próximas detecciones de giro)
      existing.lastDirection = player.direction;

      existing.label.setText(player.name || 'Player');
      // Color de nombre por facción (reactivo: si el player se une/cambia de
      // facción, su nombre se recolorea acá). Ver docs/FACTIONS.md.
      const factionColor = getFactionColor((player.faction ?? '') as FactionId);
      if (existing.label instanceof Phaser.GameObjects.Text) {
        existing.label.setColor(factionColor);
      } else if (existing.label instanceof Phaser.GameObjects.BitmapText) {
        existing.label.setTint(Phaser.Display.Color.HexStringToColor(factionColor).color);
      }
      // Re-centrar el texto después de cambiar el contenido
      // El centrado se hará en el update de renderizado donde tenemos renderX
    }

    // Actualizar animación de body según dirección / estado.

    // Para TODOS los jugadores:
    // - Cada vez que cambian de tile (movimiento válido), garantizamos al menos

    //   1 ciclo completo de caminata.

    // - Si el movimiento es bloqueado (no cambia de tile), NO se anima caminar;

    //   solo se actualiza el heading y los gráficos de cuerpo/cabeza.

    const render = this.scene.playerObjects.get(id);

    const directionForRender = render?.visualDirection ?? player.direction;

    // Reutilizar las variables ya declaradas arriba (bodyId, helmId, shieldId, weaponId)
    const currentBodyId = bodyId; // bodyId ya fue calculado arriba

    // AUTO-REPARACIÓN por frame: si el body aún no está cortado/animado (warmup async no llegó, se
    // abortó o una excepción previa lo salteó), cortarlo ahora. Idempotente y O(1) cuando ya está
    // listo. Evita el render de la lámina entera y el spam de "Texture bodyN has no frame idx".
    ensureBodyReady(this.scene, currentBodyId);

    const bodyAnimKey = getBodyAnimKey(currentBodyId, directionForRender);

    if (render) {
      // === ACTUALIZAR BODY EN TIEMPO REAL ===
      // Verificar si cambió el bodyId (equipa/desequipa armadura)
      const currentBodyKey = render.sprite.texture?.key;
      const expectedBodyKey = getBodySpriteKey(currentBodyId);
      
      if (currentBodyKey !== expectedBodyKey) {
        // Body cambió - actualizar textura (sin offset, todos centrados en el tile)
        render.sprite.setTexture(expectedBodyKey);
        render.sprite.setFrame(getBodyIdleFrame(directionForRender, currentBodyId));
        // Also update shadow texture to match the new body
        if (render.shadow) {
          render.shadow.setTexture(expectedBodyKey);
          render.shadow.setFrame(getBodyIdleFrame(directionForRender, currentBodyId));
        }
        // Update dropshadow scale to match the new body width
        if (render.dropshadow) {
          this.scene.shadows.updateDropshadowScale(render.dropshadow, currentBodyId);
        }
        render.bodyId = currentBodyId;
        render.bodyCenterOffsetX = GameScene.getBodyCenterOffsetX(currentBodyId); // Offset para centrar visualmente
        console.log(`[GameScene] Player ${id} body updated to bodyId=${currentBodyId} (offset=${render.bodyCenterOffsetX})`);
      }
      
      // Verificar si cambió el weaponId y actualizar el sprite de arma
      const currentWeaponKey = render.weapon?.texture?.key;
      // Override client-side (galería): el player local puede previsualizar
      // cualquier sheet de la carpeta sin tocar el server.
      const weaponPreviewKey =
        isLocal &&
        this.scene.localWeaponPreviewSheetKey &&
        this.scene.textures.exists(this.scene.localWeaponPreviewSheetKey)
          ? this.scene.localWeaponPreviewSheetKey
          : null;
      const expectedWeaponKey =
        weaponPreviewKey ?? (weaponId > 0 ? getWeaponSpriteKey(weaponId) : null);
      
      if (expectedWeaponKey && currentWeaponKey !== expectedWeaponKey) {
        if (render.weapon) {
          render.weapon.destroy();
        }
        const newWeaponSheetOff = getWeaponSheetOffsetForTextureKey(expectedWeaponKey);
        render.weapon = this.scene.add
          .sprite(
            render.sprite.x + GameScene.weaponOffsetX(directionForRender) + newWeaponSheetOff.x,
            render.sprite.y + GameScene.WEAPON_OFFSET_Y + newWeaponSheetOff.y,
            expectedWeaponKey!
          )
          .setOrigin(0.5, 1);
        render.weapon.setFrame(getWeaponIdleFrame(directionForRender));
        this.scene.updateEquipmentDepth(render, directionForRender, player.y);
      } else if (!expectedWeaponKey && render.weapon) {
        render.weapon.destroy();
        render.weapon = null;
      }
      
      // Verificar si cambió el helmId y actualizar el sprite de casco
      const currentHelmKey = render.helm?.texture?.key;
      const expectedHelmKey = helmId > 0 ? getHelmetSpriteKey(helmId) : null;
      if (expectedHelmKey && currentHelmKey !== expectedHelmKey) {
        if (this.scene.textures.exists(expectedHelmKey)) {
          const helmetTexture = this.scene.textures.get(expectedHelmKey);
          if (helmetTexture && helmetTexture.key !== '__MISSING') {
            if (render.helm) {
              render.helm.destroy();
            }
            const bodyBottomY = render.sprite.y;
            const headCenterY = bodyBottomY - GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM - GameScene.HEAD_FRAME_HEIGHT / 2;
            const helmCenterY = headCenterY - 8;
            render.helm = this.scene.add.sprite(render.sprite.x, helmCenterY, expectedHelmKey).setOrigin(0.5, 0.5);
            render.helm.setFrame(getHelmetFrameForDirection(directionForRender));
            this.scene.updateEquipmentDepth(render, directionForRender, player.y);
            console.log(`[GameScene] Player ${id} helm updated to helmId=${helmId}`);
          } else {
            console.warn(`[GameScene] Helmet texture ${expectedHelmKey} is missing or not ready for player ${id}`);
          }
        } else {
          console.warn(`[GameScene] Helmet texture ${expectedHelmKey} does not exist for player ${id}`);
        }
      } else if (helmId === 0 && render.helm) {
        render.helm.destroy();
        render.helm = null;
        console.log(`[GameScene] Player ${id} helm removed`);
      }

      // Verificar si cambió el bootId y actualizar el sprite de botas
      const currentBootKey = render.boots?.texture?.key;
      const expectedBootKey = bootId > 0 ? getBootSpriteKey(bootId) : null;
      if (expectedBootKey && currentBootKey !== expectedBootKey) {
        if (render.boots) {
          render.boots.destroy();
        }
        render.boots = this.scene.add.sprite(render.sprite.x, render.sprite.y, expectedBootKey).setOrigin(0.5, 1);
        render.boots.setFrame(getBootFrameForDirection(directionForRender));
        this.scene.updateEquipmentDepth(render, directionForRender, player.y);
        console.log(`[GameScene] Player ${id} boots updated to bootId=${bootId}`);
      } else if (bootId === 0 && render.boots) {
        render.boots.destroy();
        render.boots = null;
        console.log(`[GameScene] Player ${id} boots removed`);
      }

      // Verificar si cambió el shieldId y actualizar el sprite de escudo
      const currentShieldKey = render.shield?.texture?.key;
      const expectedShieldKey = shieldId > 0 ? getShieldSpriteKey(shieldId) : null;
      
      if (shieldId > 0 && expectedShieldKey && currentShieldKey !== expectedShieldKey) {
        if (this.scene.textures.exists(expectedShieldKey)) {
          const shieldTexture = this.scene.textures.get(expectedShieldKey);
          if (shieldTexture && shieldTexture.key !== '__MISSING') {
            if (render.shield) {
              render.shield.destroy();
            }
            render.shield = this.scene.add
              .sprite(render.sprite.x + GameScene.SHIELD_OFFSET_X, render.sprite.y, expectedShieldKey)
              .setOrigin(0.5, 1);
            render.shield.setFrame(getShieldIdleFrame(directionForRender));
            this.scene.updateEquipmentDepth(render, directionForRender, player.y);
            console.log(`[GameScene] Player ${id} shield updated to shieldId=${shieldId}`);
          } else {
            console.warn(`[GameScene] Shield texture ${expectedShieldKey} is missing or not ready for player ${id}`);
          }
        } else {
          console.warn(`[GameScene] Shield texture ${expectedShieldKey} does not exist for player ${id}`);
        }
      } else if (shieldId === 0 && render.shield) {
        render.shield.destroy();
        render.shield = null;
        console.log(`[GameScene] Player ${id} shield removed`);
      }

      // Verificar si cambió el headId y actualizar el sprite de cabeza. headId === 0 =
      // head oculta (bodies de NPC que ya traen cabeza, vía SET_HEAD_HIDDEN). El head se
      // crea una vez al spawnear, así que sin este bloque no se ocultaría/reaparecería.
      const expectedHeadKey = headId > 0 ? getHeadSpriteKey(headId) : null;
      const currentHeadKey = render.head?.texture?.key ?? null;
      if (expectedHeadKey && currentHeadKey !== expectedHeadKey) {
        if (this.scene.textures.exists(expectedHeadKey)) {
          if (render.head) render.head.destroy();
          const headCenterY =
            render.sprite.y - GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM - GameScene.HEAD_FRAME_HEIGHT / 2;
          render.head = this.scene.add
            .sprite(render.sprite.x, headCenterY, expectedHeadKey)
            .setOrigin(0.5, 0.5)
            .setScrollFactor(1);
          render.head.setFrame(getHeadFrameForDirection(directionForRender));
          this.scene.updateEquipmentDepth(render, directionForRender, player.y);
        }
      } else if (!expectedHeadKey && render.head) {
        render.head.destroy();
        render.head = null;
        console.log(`[GameScene] Player ${id} head hidden`);
      }
      // === FIN ACTUALIZAR EQUIPAMIENTO ===

      const now = this.scene.time.now;

      // Detectar si en ESTE frame cambió de tile (comparando contra el tracking anterior)
      let movedThisFrame = false;

      // =====================================================================
      // ESTILO ARGENTUM: Para el jugador LOCAL, lastTileX/Y es la posición
      // PREDICHA por el cliente (se actualiza en startLocalMovement).
      // Para jugadores REMOTOS, lastTileX/Y se sincroniza con el servidor.
      // =====================================================================
      const isLocalPlayer = id === this.scene.localSessionId;
      
      if (!isLocalPlayer && (render.lastTileX !== player.x || render.lastTileY !== player.y)) {
        // Huella del jugador REMOTO: en el tile que dejó atrás. Solo si avanzó
        // exactamente 1 tile (no en snaps/teleports/correcciones). `noFootsteps` suprime el decal
        // visual y `noWalkNoise` el SFX (ejes separados; spawn gatea cada uno adentro).
        const rp = player as unknown as {
          noFootsteps?: number;
          noWalkNoise?: number;
          footstepDuration?: number;
        };
        this.scene.footsteps.spawnRemoteEntityFootstep(
          render.lastTileX,
          render.lastTileY,
          player.x,
          player.y,
          DEFAULT_PLAYER_FOOTSTEP_ID,
          rp.footstepDuration ?? FOOTSTEP_BASE_DURATION_MS,
          !!rp.noWalkNoise,
          !!rp.noFootsteps,
          id
        );
        render.lastTileX = player.x;
        render.lastTileY = player.y;
        movedThisFrame = true;
      }

      // =====================================================================
      // ANIMACIÓN SIMPLE (estilo argentumonline.io)
      // =====================================================================
      // Animar caminata solo si hay movimiento NORMAL en progreso
      // (moveDuration > 0 AND moveSpeed > 0)
      // 
      // NO animar durante correcciones del servidor (moveSpeed = 0)
      // =====================================================================
      // El jugador local sigue animando durante la gracia de `walkUntil` aunque
      // `moveDuration` caiga a 0 por un frame entre steps (ver startLocalMovement).
      // Así la anim no se detiene ni reinicia a col 0 en caminata continua.
      const shouldAnimateWalk =
        (render.moveDuration > 0 && render.moveSpeed > 0) ||
        (isLocalPlayer && now < render.walkUntil);

      // =====================================================================
      // TIME-BASED LERP MOVEMENT (same as NPCs for perfectly smooth motion)
      // 
      // Instead of velocity-based movement, we use:
      //   progress = elapsed_time / move_duration
      //   position = start + (target - start) * progress
      // 
      // This guarantees the movement takes EXACTLY moveDuration milliseconds,
      // eliminating visual "jumps" between tiles.
      // =====================================================================
      
      // =====================================================================
      // INTERPOLACIÓN (estilo argentumonline.io - usando delta time acumulado)
      // =====================================================================
      // - Usa elapsed += delta en lugar de timestamps
      // - NO inicia nuevos movimientos - solo procesa la transición
      // - Cuando termina, simplemente resetea el estado
      // - El siguiente movimiento lo inicia tryStartLocalMovement() en el próximo frame
      // =====================================================================
      if (render.moveDuration > 0) {
        const isLocalPlayer = id === this.scene.localSessionId;
        const entityMoveSpeed = (player as any).moveSpeed ?? 1.0;
        
        // Para remotos, ajustar duración por moveSpeed
        const effectiveDuration = isLocalPlayer 
          ? render.moveDuration 
          : (entityMoveSpeed > 0 ? render.moveDuration / entityMoveSpeed : render.moveDuration);
        
        // Acumular delta time (estilo argentumonline.io)
        render.moveElapsed += this.scene.currentDelta;
        
        // Calcular posición interpolada (lineal, sin easing)
        const diff_x = render.targetWorldX - render.startWorldX;
        const diff_y = render.targetWorldY - render.startWorldY;
        const value_x = render.startWorldX + (diff_x / effectiveDuration) * render.moveElapsed;
        const value_y = render.startWorldY + (diff_y / effectiveDuration) * render.moveElapsed;
        
        // Verificar si el movimiento terminó (igual que transition.js de argentumonline.io)
        const reachedEnd = 
          render.moveElapsed >= effectiveDuration ||
          (diff_x > 0 && value_x >= render.targetWorldX) ||
          (diff_x < 0 && value_x <= render.targetWorldX) ||
          (diff_y > 0 && value_y >= render.targetWorldY) ||
          (diff_y < 0 && value_y <= render.targetWorldY);
        
        if (reachedEnd) {
          // Movimiento terminó - snap a posición final y resetear
          render.currentWorldX = render.targetWorldX;
          render.currentWorldY = render.targetWorldY;
          render.moveDuration = 0;
          render.moveSpeed = 0;
          render.moveElapsed = 0;

          // =====================================================================
          // INMEDIATAMENTE intentar iniciar el siguiente movimiento
          // =====================================================================
          // Si hay tecla presionada, iniciar el siguiente movimiento en el MISMO frame
          // para evitar la "pausa" de un frame entre movimientos.
          // Esto es crítico para movimiento continuo fluido.
          // =====================================================================
          if (isLocalPlayer && !this.scene.chatIsFocused) {
            this.scene.movement.tryStartLocalMovement();
          }

          // =====================================================================
          // JUGADORES REMOTOS: consumir stepQueue para movimiento continuo fluido
          // =====================================================================
          // Sin esto, los remotos se "frenan" al terminar cada step y esperan
          // el próximo onChange del servidor, causando saltos visibles.
          // =====================================================================
          if (!isLocalPlayer && render.stepQueue.length > 0) {
            const nextStep = render.stepQueue.shift()!;
            const nextTargetX = nextStep.x * TILE_SIZE + TILE_SIZE / 2;
            const nextTargetY = nextStep.y * TILE_SIZE + TILE_SIZE / 2;
            render.startWorldX = render.currentWorldX;
            render.startWorldY = render.currentWorldY;
            render.targetWorldX = nextTargetX;
            render.targetWorldY = nextTargetY;
            render.moveElapsed = 0;
            render.moveDuration = GameScene.REMOTE_STEP_MS;
            render.moveSpeed = GameScene.PLAYER_MOVE_SPEED;
            render.visualDirection = nextStep.dir;
            const absDx = Math.abs(nextTargetX - render.currentWorldX);
            const absDy = Math.abs(nextTargetY - render.currentWorldY);
            render.activeAxis = absDx >= absDy ? 'x' : 'y';
            render.cornerReady = false;
          }
        } else {
          // Movimiento en progreso - actualizar posición
          render.currentWorldX = value_x;
          render.currentWorldY = value_y;
        }
      }

      // =====================================================================
      // RENDERING FIX: For the LOCAL player, use scrollFactor(0) and position
      // at screen center. This ensures the player NEVER flickers - the sprite
      // is always at the exact same screen position, and the world moves around it.
      // 
      // For REMOTE players/entities, use world coordinates normally.
      // =====================================================================
      
      // =====================================================================
      // ANTI-VIBRATION: Round all positions to prevent sub-pixel jitter.
      // This ensures the camera scroll (also rounded) matches sprite positions exactly.
      // =====================================================================
      const renderX = Math.round(render.currentWorldX);
      const renderY = Math.round(render.currentWorldY);
      
      // Body offset for visual centering (varies per body type)
      const bodyOffsetX = render.bodyCenterOffsetX;

      const bodyY = renderY + GameScene.BODY_OFFSET_Y;
      const bodyBottomY = bodyY + GameScene.BODY_FRAME_HEIGHT / 2;
      const headCenterY = bodyBottomY - GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM - GameScene.HEAD_FRAME_HEIGHT / 2;
      
      // =====================================================================
      // BREATHING EFFECT: Subtle vertical scale oscillation for players
      // Only applies when player is IDLE (not moving)
      // Simulates breathing with intervals: breathe for ~2s, then wait 5s, repeat
      // ScaleX stays at 1.0, only scaleY oscillates (vertical breathing)
      // =====================================================================
      const BREATHING_DURATION = 4000; // ms que dura la animación de respiración (más lenta/profunda)
      const BREATHING_INTERVAL = 2500; // ms de espera entre respiraciones (el doble de frecuente)
      const BREATHING_CYCLE = BREATHING_DURATION + BREATHING_INTERVAL; // Ciclo completo
      const BREATHING_SCALE_AMPLITUDE = 0.015; // Variación de escala vertical (1.5% = muy sutil)
      const BREATHING_IDLE_DELAY = 5000; // el jugador respira recién tras 5 s quieto en el mismo lugar
      // "Idle" para el gate: los remotos usan el estado del server (IDLE); el jugador LOCAL casi nunca
      // queda en IDLE server por la predicción de cliente, así que usamos su señal de cliente (no está
      // animando caminata) para saber que está quieto.
      const isIdle = isLocalPlayer
        ? !shouldAnimateWalk && player.state !== EntityState.DEAD
        : player.state === EntityState.IDLE;
      // Cuánto lleva quieto: se reinicia al moverse. Recién respira pasado BREATHING_IDLE_DELAY.
      if (isIdle) {
        if (render.idleSince === null) render.idleSince = now;
      } else {
        render.idleSince = null;
      }

      let breathingScaleY = 1.0;
      if (render.idleSince !== null && now - render.idleSince >= BREATHING_IDLE_DELAY) {
        // Fase con Date.now() (reloj compartido entre clientes) + offset DETERMINÍSTICO por id: todos
        // los clientes ven la MISMA animación para la misma entidad. Ver util/hash.ts.
        const cycleTime = (Date.now() + render.breathingOffset) % BREATHING_CYCLE;
        if (cycleTime < BREATHING_DURATION) {
          const breathingProgress = cycleTime / BREATHING_DURATION; // 0 a 1
          breathingScaleY = 1.0 + Math.sin(breathingProgress * Math.PI) * BREATHING_SCALE_AMPLITUDE;
        }
      }

      // =====================================================================
      // ALL PLAYERS (local and remote): Use world coordinates with scrollFactor(1)
      // This ensures proper depth sorting with decorations and other entities.
      // The camera follows the local player via updateCameraPosition().
      // =====================================================================
      render.sprite.setScrollFactor(1);
      render.sprite.setPosition(renderX + bodyOffsetX, bodyBottomY);
      render.sprite.setScale(1.0, breathingScaleY); // Solo escala vertical, no horizontal
      
      // Update shadow position - projects from entity feet like clock hands
      if (render.shadow) {
        const shadowDepth = this.scene.getEntityBaseDepthFromTileY(Math.floor(renderY / TILE_SIZE)) + GameScene.SHADOW_DEPTH_DELTA;
        
        // Shadow projects from the entity's feet position
        // Distance increases with shadow length (longer at dawn/dusk)
        const maxDistance = 16; // Maximum shadow distance in pixels
        const shadowDistance = this.scene.shadowLength * maxDistance;
        
        // Calculate offset using trigonometry (angle rotates like clock hands)
        const shadowOffsetX = Math.cos(this.scene.shadowAngle) * shadowDistance;
        const shadowOffsetY = Math.sin(this.scene.shadowAngle) * shadowDistance * 0.5; // Flatten Y for isometric look
        
        // Shadow position: at entity feet + offset
        const shadowX = renderX + bodyOffsetX + shadowOffsetX;
        const shadowY = bodyBottomY + shadowOffsetY + 2; // +2 to place slightly below feet
        
        // Scale: stretch shadow in the direction it's pointing
        const baseScale = 0.8;
        const stretchFactor = 1 + this.scene.shadowLength * 0.6;
        
        render.shadow.setScrollFactor(1);
        render.shadow.setPosition(shadowX, shadowY);
        render.shadow.setScale(baseScale * stretchFactor, baseScale * 0.6);
        render.shadow.setAlpha(this.scene.shadowAlpha);
        render.shadow.setDepth(shadowDepth);
      }

      // Fade de sombra: sin sombra bajo techo de casa (área `interior`), con ease suave.
      const playerShadowTarget = this.scene.mapAmbient.entityUnderRoof(renderX + bodyOffsetX, bodyBottomY) ? 0 : 1;
      render.shadowFade += (playerShadowTarget - render.shadowFade) * GameScene.SHADOW_FADE_STEP;

      // Update dropshadow position (independent system)
      // Si el player es el target seleccionado (incluido self-target), ocultar su
      // dropshadow para que no compita con el retículo.
      if (render.dropshadow) {
        const isTargeted = this.scene.targets.selectedTargetId === id;
        render.dropshadow.setVisible(!isTargeted && render.shadowFade > 0.02);
        if (!isTargeted) {
          // Depth fijo bajo la banda de decoraciones: la sombra nunca pisa árboles.
          render.dropshadow.setScrollFactor(1);
          render.dropshadow.setPosition(renderX + bodyOffsetX, bodyBottomY + GameScene.DROPSHADOW_OFFSET_Y);
          render.dropshadow.setDepth(GameScene.DROPSHADOW_GROUND_DEPTH);
          render.dropshadow.setAlpha(GameScene.DROPSHADOW_ALPHA * render.shadowFade);
        }
      }
      
      // Update entity light position
      this.scene.entityLight.updateEntityLight(id, renderX + bodyOffsetX, bodyBottomY);
      
      if (render.head) {
        render.head.setScrollFactor(1);
        // La cabeza NO usa bodyCenterOffsetX: se centra sola en el tile (renderX). El offset es
        // para centrar el BODY (criaturas anchas); aplicárselo a la cabeza la corría al cambiar
        // de body. La cabeza se ancla al tile, no al contenido del body.
        render.head.setPosition(renderX, headCenterY);
        render.head.setScale(1.0, breathingScaleY); // Aplicar efecto de respiración a la cabeza
      }

      if (render.helm) {
        render.helm.setScrollFactor(1);
        render.helm.setPosition(renderX, headCenterY - 8); // ídem cabeza: sin bodyCenterOffsetX
        render.helm.setScale(1.0, breathingScaleY); // Aplicar efecto de respiración al casco
      }
      
      if (render.boots) {
        render.boots.setScrollFactor(1);
        render.boots.setPosition(renderX + bodyOffsetX, bodyBottomY);
        render.boots.setScale(1.0, breathingScaleY); // Solo escala vertical
      }
      
      render.label.setScrollFactor(1);
      this.scene.labels.setEntityLabelPosition(render.label, renderX, bodyBottomY + 2);
      // Reglas de "almas" (ocultar nombres de muertos salvo mod / misma
      // facción) + regla naval (navegando solo hover/target).
      render.label.setVisible(this.scene.isPlayerNameVisible(player, isLocal, id, render));

      render.arrow.setScrollFactor(1);
      render.arrow.setPosition(renderX, renderY);
      
      if (render.shield) {
        render.shield.setScrollFactor(1);
        render.shield.setPosition(renderX + bodyOffsetX + GameScene.SHIELD_OFFSET_X, bodyBottomY);
        render.shield.setScale(1.0, breathingScaleY); // Solo escala vertical
      }
      
      if (render.weapon) {
        const weaponSheetOff = getWeaponSheetOffsetForTextureKey(render.weapon.texture.key);
        render.weapon.setScrollFactor(1);
        render.weapon.setPosition(
          renderX + bodyOffsetX + GameScene.weaponOffsetX(directionForRender) + weaponSheetOff.x,
          bodyBottomY + GameScene.WEAPON_OFFSET_Y + weaponSheetOff.y
        );
        render.weapon.setScale(1.0, breathingScaleY); // Solo escala vertical
      }
      
      // Head and helm also breathe (vertical only)
      if (render.head) {
        render.head.setScale(1.0, breathingScaleY);
      }
      
      if (render.helm) {
        render.helm.setScale(1.0, breathingScaleY);
      }
      
      for (const anim of render.activeAnimations) {
        anim.sprite.setScrollFactor(1);
        anim.sprite.setPosition(renderX + bodyOffsetX, bodyY + anim.offsetY);
        anim.sprite.setDepth(anim.back ? render.sprite.depth - 1 : render.sprite.depth + 100);
      }

      // Actualizar orientación de la flecha
      this.scene.updateArrowOrientation(render.arrow, directionForRender);

      // =====================================================================
      // DEPTH UPDATE: Update depth every frame based on interpolated Y position.
      // This ensures entities are sorted correctly with decorations:
      // - Lower Y (higher on screen) = behind decorations
      // - Higher Y (lower on screen) = in front of decorations
      // =====================================================================
      const interpolatedTileY = Math.floor(renderY / TILE_SIZE);
      this.scene.updateEquipmentDepth(render, directionForRender, interpolatedTileY);

      // Actualizar highlight del tile del jugador local en tiempo real
      // ESTILO ARGENTUM: El tile "lógico" es el tile DESTINO (lastTileX/Y)
      // La posición visual es el tile destino + offset de movimiento
      if (isLocal && this.scene.showPlayerTile) {
        // Verde = tile destino del cliente (lastTileX/Y) - donde el cliente dice que está
        this.scene.tileHighlight.updatePlayerTileHighlight(render.lastTileX, render.lastTileY);
        // Rojo = tile del servidor (serverTileX/Y) - donde el servidor dice que está
        this.scene.tileHighlight.updateServerTileHighlight(render.serverTileX, render.serverTileY);
      }
      
      // Crosshair en el centro de la pantalla (siempre actualizado para el jugador local)
      if (isLocal) {
        this.scene.updateCrosshair();
      }

      if (shouldAnimateWalk) {
        // Tempo escalado por moveSpeed (como ApplySpeedingToChar de AO): el
        // walk se acelera/frena junto con la velocidad de movimiento del char.
        const walkTimeScale = ((player as any).moveSpeed as number | undefined) || 1.0;
        render.sprite.anims.timeScale = walkTimeScale;
        if (render.shadow) {
          render.shadow.anims.timeScale = walkTimeScale;
        }

        // Animar cuerpo (naked body). GUARD a prueba de balas: la anim de caminar puede
        // (a) no existir aún (warmup chunked) o (b) existir pero estar VACÍA (0 frames:
        // sheet miscut / columnas inválidas). En ambos casos Phaser.anims.play() crashea
        // (`getFirstTick` lee `currentFrame.duration` de undefined) y, como corre dentro
        // del forEach de players en update(), rompe el frame ENTERO (render "roto"). Caemos
        // a un frame idle estático y envolvemos el play en try/catch como red final.
        const bodyAnim = this.scene.anims.exists(bodyAnimKey) ? this.scene.anims.get(bodyAnimKey) : null;
        if (!bodyAnim || bodyAnim.frames.length === 0) {
          warnBadBodyAnim(bodyAnimKey, currentBodyId, bodyAnim ? 'sin-frames' : 'inexistente');
          render.sprite.anims.stop();
          render.sprite.setFrame(getBodyIdleFrame(directionForRender, currentBodyId));
          if (render.shadow) {
            render.shadow.anims.stop();
            render.shadow.setFrame(getBodyIdleFrame(directionForRender, currentBodyId));
          }
        } else if (render.sprite.anims.currentAnim?.key !== bodyAnimKey || !render.sprite.anims.isPlaying) {
          // Solo llamar a play() si la animación es diferente o no está reproduciéndose.
          // Preservar la FASE al cambiar de dirección en movimiento (SyncGrhPhase
          // del cliente oficial de AO): si ya venía caminando con otra fila, la
          // anim nueva arranca en la misma columna en vez de resetear a col 0.
          let startFrame = 0;
          const prevKey = render.sprite.anims.currentAnim?.key;
          if (render.sprite.anims.isPlaying && prevKey && prevKey !== bodyAnimKey) {
            const idx = parseInt(render.sprite.frame.name, 10);
            if (Number.isFinite(idx)) {
              const lateral =
                directionForRender === Direction.LEFT || directionForRender === Direction.RIGHT;
              const bodyCols = getBodyColumns(currentBodyId);
              const walkLen = lateral ? getBodyLateralColumns(currentBodyId) : bodyCols;
              startFrame = (idx % bodyCols) % walkLen;
            }
          }
          try {
            render.sprite.anims.play({ key: bodyAnimKey, startFrame }, true);
            // Sync shadow animation with body
            if (render.shadow) {
              render.shadow.anims.play({ key: bodyAnimKey, startFrame }, true);
            }
          } catch {
            warnBadBodyAnim(bodyAnimKey, currentBodyId, 'play-throw');
            render.sprite.anims.stop();
            render.sprite.setFrame(getBodyIdleFrame(directionForRender, currentBodyId));
          }
        }

        // Estilo AO (clon de Argentum Online): el arma/escudo recorren TODOS los
        // frames de su animación de caminar, SINCRONIZADOS con el body — igual que
        // ArcaneAO, que arranca WeaponWalk junto al body en loop infinito. Acá se
        // logra frame-lockeando el arma/escudo al frame EXACTO del body cada tick:
        // copian la misma columna que el cuerpo, así van siempre en el mismo cuadro
        // (imposible que se desincronicen aunque se equipe a mitad de caminata).
        const currentWeaponId = ((player as any).weaponId as number | undefined) ?? 0;
        const currentShieldId = ((player as any).shieldId as number | undefined) ?? 0;
        const bodyFrameIndex = parseInt(render.sprite.frame.name, 10);
        if (Number.isFinite(bodyFrameIndex)) {
          if (render.weapon && (currentWeaponId > 0 || (isLocal && this.scene.localWeaponPreviewSheetKey))) {
            render.weapon.anims.stop();
            render.weapon.setFrame(getWeaponFrameFromBodyIndex(bodyFrameIndex, getBodyColumns(currentBodyId)));
          }
          if (render.shield && currentShieldId > 0) {
            render.shield.anims.stop();
            render.shield.setFrame(getShieldFrameFromBodyIndex(bodyFrameIndex, getBodyColumns(currentBodyId)));
          }
        }
      } else {
        // Idle: detener animación y poner primer frame de la fila correspondiente
        render.sprite.anims.stop();
        render.sprite.anims.timeScale = 1;
        if (render.shadow) {
          render.shadow.anims.timeScale = 1;
        }

        const idleFrame = getBodyIdleFrame(directionForRender, currentBodyId);

        render.sprite.setFrame(idleFrame);

        // Sync all shadow frames with their counterparts (idle state)
        if (render.shadow) {
          render.shadow.anims.stop();
          render.shadow.setFrame(idleFrame);
        }

        if (render.shield) {
          render.shield.anims.stop();
          render.shield.setFrame(getShieldIdleFrame(directionForRender));
        }

        if (render.weapon) {
          render.weapon.anims.stop();
          render.weapon.setFrame(getWeaponIdleFrame(directionForRender));
        }
      }

      // Actualizar frame de cabeza según heading (gráfico estático por dirección)

      if (render.head) {
        render.head.setFrame(getHeadFrameForDirection(directionForRender));
      }

      // Actualizar frame de casco según heading (se dibuja encima de la cabeza)
      if (render.helm) {
        render.helm.setFrame(getHelmetFrameForDirection(directionForRender));
      }

      // Ajustar profundidades (weapon/shield delante/detrás según heading)

      this.scene.updateEquipmentDepth(render, directionForRender, player.y);

      // Muerte: reemplazar el render del personaje por un "alma" de partículas.
      // Va ANTES de la proyección de sombra: al ocultar las capas reales,
      // updateCastShadow ve `real.visible === false` y esconde los gemelos solo.
      this.scene.updatePlayerSoulVisuals(
        render,
        player.state === EntityState.DEAD,
        renderX,
        bodyBottomY,
        player.faction ?? ''
      );

      // Proyección de sombra: todas las capas reales ya tienen textura/frame/posición
      // final este frame, así que la silueta sale sincronizada con la animación.
      if (render.castShadow) {
        this.scene.shadows.updateCastShadow(
          render.castShadow,
          {
            body: render.sprite,
            head: render.head,
            weapon: render.weapon,
            shield: render.shield,
            helm: render.helm,
            boots: render.boots,
          },
          renderX + bodyOffsetX,
          bodyBottomY,
          render.shadowFade
        );
      }

      // Si hay un giro pendiente, aplicarlo cuando se haya llegado al tile anterior
      if (render.pendingDirection !== null && render.stepQueue.length === 0) {
        render.visualDirection = render.pendingDirection;
        render.lastDirection = render.pendingDirection;
        render.pendingDirection = null;
      }
    }

    // Actualizar HUD (posición + FPS) para el jugador local

    if (isLocal) {
      const fps = Math.round(this.scene.game.loop.actualFps || 0);

      if (this.scene.fpsText) {
        this.scene.fpsText.setText(`${fps}`);
      }

      if (this.scene.coordText) {
        this.scene.coordText.setText(`${this.scene.currentMapId}, ${player.x}, ${player.y}`);
      }

      // El highlight del tile se actualiza en el loop de renderizado arriba
    }

    // F4: sync de auras VFX según server state. Hace diff con el set actual
    // y arranca/para emitters via ParticleManager. Navegando (sailing) los FX
    // se OCULTAN (el barco tapa al personaje); al desembarcar, sailing vuelve
    // a false y este mismo diff los re-spawnea. Ver docs/WATER_AND_BOATS.md.
    const auraFxIds = (player as unknown as { auraFxIds?: { length: number; [k: number]: string } }).auraFxIds;
    if (auraFxIds && typeof auraFxIds.length === 'number') {
      const sailing = (player as unknown as { sailing?: boolean }).sailing === true;
      const ids: string[] = [];
      if (!sailing) {
        for (let i = 0; i < auraFxIds.length; i++) ids.push(auraFxIds[i]);
      }
      // suppressExpireFx: ocultar por navegar NO es una expiración de buff.
      this.scene.entityVfx.syncAuraEmittersFor(id, ids, sailing);
    }

    // Pérdida de movilidad: raíces (inmovilizado) / tint pétreo (paralizado).
    this.scene.entityVfx.syncImpairVisualsFor(id, (player as unknown as { impairState?: number }).impairState ?? 0);
    // Invisibilidad: cloak (alpha + shimmer) + nombre según relación con el observador.
    this.scene.entityVfx.syncInvisibilityVisualsFor(id, (player as unknown as { invisibleState?: number }).invisibleState ?? 0, true);
    this.scene.stateVfx.syncTauntVisualsFor(id, (player as unknown as { taunted?: number }).taunted ?? 0);

    // F5: sync de VFX de items equipados (weapon/body/helm/shield/boot).
    const playerAny = player as unknown as Record<string, number | undefined>;
    this.scene.entityVfx.syncEquippedVfxFor(id, {
      weaponId: playerAny.weaponId,
      bodyId: playerAny.bodyId,
      helmId: playerAny.helmId,
      shieldId: playerAny.shieldId,
      bootId: playerAny.bootId,
    });
  }

  /**
   * Calcula el bodyId desnudo basado en race y sex.
   * Fallback para cuando el servidor no envía bodyId directamente.
   * Delega al helper compartido para no duplicar el mapeo (NAKED_BODY_CONFIG).
   */
  public calculateNakedBodyId(race: string | undefined, sex: string | undefined): number {
    return getNakedBodyId(
      (race ?? 'human') as CharacterRaceId,
      (sex ?? 'male') as CharacterSexId
    );
  }

  public removePlayer(id: string): void {
    const existing = this.scene.playerObjects.get(id);

    if (!existing) return;

    existing.sprite.destroy();

    // Destroy cast shadow (silueta proyectada multi-capa)
    this.scene.shadows.destroyCastShadow(existing.castShadow);

    // Destroy shadow sprite
    existing.shadow?.destroy();

    // Destroy dropshadow sprite
    existing.dropshadow?.destroy();

    existing.head?.destroy();

    existing.helm?.destroy();

    existing.boots?.destroy();

    this.scene.labels.destroyEntityLabel(existing.label);

    existing.arrow.destroy();

    existing.shield?.destroy();

    existing.weapon?.destroy();

    existing.cameraTarget?.destroy();

    // Destruir el alma de partículas (si el player murió y estaba en modo alma)
    existing.soul?.destroy();

    // Destruir animaciones activas

    for (const anim of existing.activeAnimations) {
      anim.sprite.destroy();
    }

    existing.activeAnimations = [];

    // Remove player light source
    this.scene.entityLight.removeEntityLight(id);

    // F4: limpiar auras sincronizadas del entity removido
    this.scene.entityVfx.clearAuraEmittersFor(id);
    // F5: limpiar VFX de equipped items
    this.scene.entityVfx.clearEquippedVfxFor(id);
    // Pérdida de movilidad: limpiar raíces/tint del entity removido
    this.scene.entityVfx.clearImpairVisualsFor(id);
    // Invisibilidad: restaurar render (por si se removió estando invisible).
    this.scene.entityVfx.clearInvisibilityVisualsFor(id);
    // Protección de aparición: remover halo + tween si seguía activo (silent, sin destello)
    this.scene.stateVfx.stopSpawnProtectionVisual(id, true);

    this.scene.playerObjects.delete(id);
  }
}
